import { map, Observable } from 'rxjs';

import {
  Category,
  FinnairAmount,
  FinnairChangeEligibility,
  FinnairEligibilities,
  FinnairOrder,
  FinnairPassengerItem,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceCategoryPriceItem,
  FinnairServiceItem,
  FinnairTotalPricesDetails,
} from '@fcom/dapi/api/models';
import { isPresent, PreciseNumber } from '@fcom/core/utils';
import { PriceService } from '@fcom/dapi/interfaces';

import { combineServices, filterServices } from '../modules/ancillaries/utils/ancillary.utils';
import { isBoundBasedCategory, isBundle } from '../modules/ancillaries/utils/category.utils';
import { TravelInsuranceEligibilityId } from '../../manage-booking/enums';

export type ThirdPartyEligibilityKey = keyof Pick<
  FinnairEligibilities,
  'carRental' | 'airportTransfer' | 'hotel' | 'activities'
>;

export const getChangeableBoundIdsFromOrder = (order: FinnairOrder | null | undefined): string[] => {
  const changeEligibilities: FinnairChangeEligibility[] = order?.eligibilities?.change ?? [];

  return changeEligibilities.filter(({ isAllowedToUse }) => isAllowedToUse).map(({ id }) => id);
};

export const getPrimaryPassengerLastName = (order: FinnairOrder): string | undefined => {
  const primaryPassenger =
    order.passengers?.find((passenger) => passenger.id === order.primaryTravelerId) ?? order.passengers?.[0];
  return primaryPassenger?.lastName;
};

export const getLastPaidServices = (order: FinnairOrder): FinnairServiceItem[] => {
  const purchasedTSMS =
    order.checkouts?.sort((a, b) => +a.timestamp - +b.timestamp)[0]?.items.map((item) => item.id) ?? [];

  if (purchasedTSMS.length === 0) {
    return order.services.pending;
  }

  const services = filterServices(
    combineServices([order.services.included, order.services.pending]),
    (service: FinnairPassengerServiceSelectionItem) => purchasedTSMS.includes(service.id)
  );

  return order.otherInformation?.serviceTotalsCalculationCorrected ? dedupeServicesById(services) : services;
};

const dedupeServicesById = (services: FinnairServiceItem[]): FinnairServiceItem[] => {
  const seenIds = new Set<string>();
  return services.map((service) => dedupeService(service, seenIds)).filter((service) => service.bounds.length > 0);
};

const dedupeService = (service: FinnairServiceItem, seenIds: Set<string>) => {
  const bounds = service.bounds
    .map((bound) => dedupeBound(bound, service.category, seenIds))
    .filter((bound) => bound.segments.length > 0);
  const remainingServices = bounds
    .flatMap((bound) => bound.segments)
    .flatMap((segment) => segment.passengers)
    .flatMap((passenger) => passenger.services);
  return {
    ...service,
    quantity: bounds.reduce((count, bound) => count + (bound.quantity ?? 0), 0),
    totalPrice: sumAmounts(bounds.map((bound) => bound.totalPrice)),
    totalPoints: remainingServices.reduce((sum, s) => sum + (s.points ?? 0), 0),
    bounds,
  };
};

const dedupeBound = (bound: FinnairServiceItem['bounds'][number], category: Category, seenIds: Set<string>) => {
  const segments = bound.segments
    .map((segment) => dedupeSegment(segment, seenIds))
    .filter((segment) => segment.passengers.length > 0);
  const divider = isBoundBasedCategory(category) ? segments.length : 1;
  return {
    ...bound,
    quantity: divider ? Math.floor(segments.reduce((count, segment) => count + segment.quantity, 0) / divider) : 0,
    totalPrice: sumAmounts(segments.map((segment) => segment.totalPrice)),
    originalTotalPrice: sumAmounts(segments.map((segment) => segment.originalTotalPrice)),
    segments,
  };
};

const dedupeSegment = (segment: FinnairServiceItem['bounds'][number]['segments'][number], seenIds: Set<string>) => {
  const passengers = segment.passengers
    .map((passenger) => dedupePassenger(passenger, seenIds))
    .filter((passenger) => passenger.services.length > 0);
  return {
    ...segment,
    quantity: passengers.reduce((count, passenger) => count + passenger.quantity, 0),
    totalPrice: sumAmounts(passengers.map((passenger) => passenger.totalPrice)),
    originalTotalPrice: sumAmounts(passengers.map((passenger) => passenger.originalTotalPrice)),
    passengers,
  };
};

const dedupePassenger = (
  passenger: FinnairServiceItem['bounds'][number]['segments'][number]['passengers'][number],
  seenIds: Set<string>
) => {
  const passengerServices = passenger.services.filter((s) => {
    if (seenIds.has(s.id)) {
      return false;
    }
    seenIds.add(s.id);
    return true;
  });
  return {
    ...passenger,
    quantity: passengerServices.length,
    totalPrice: sumAmounts(passengerServices.map(({ totalPrice }) => totalPrice)),
    originalTotalPrice: sumAmounts(passengerServices.map(({ originalTotalPrice }) => originalTotalPrice)),
    services: passengerServices,
  };
};

const sumAmounts = (amounts: Array<FinnairAmount | undefined>): FinnairAmount | undefined => {
  const items = amounts.filter((predicate): predicate is FinnairAmount => Boolean(predicate));
  if (items.length === 0) {
    return undefined;
  }
  const amount = items
    .reduce((total, price) => total.add(PreciseNumber.fromString(price.amount)), PreciseNumber.ZERO)
    .toString();
  return {
    amount,
    currencyCode: items[0].currencyCode,
  };
};

export const getLastPaidPrices = (order: FinnairOrder): FinnairTotalPricesDetails | undefined => {
  const lastCheckoutId = order.checkouts?.[0]?.id;
  return order.prices.checkouts?.find((checkout) => checkout.id === lastCheckoutId)?.prices ?? order.prices.pending;
};

export const hasPassengers = (passengers: FinnairPassengerItem[]): boolean =>
  isPresent(passengers) && passengers.length > 0;

export const getPassengerServices = (
  pricesForCategory: FinnairServiceCategoryPriceItem[] = [],
  passenger: FinnairPassengerItem
): PriceService[] => {
  const sortOrder: string[] = ['seat', 'baggage', 'meal', 'wifi', 'travelComfort', 'lounge', 'cover'].reverse();
  return pricesForCategory
    .map((c): PriceService => {
      const name = `${isBundle(c.category) ? 'bundle' : c.category}Count`;
      const count = c.totalPerPax[passenger.id]?.quantity ?? 0;
      const totalAmount = c.totalPerPax[passenger.id]?.price?.totalAmount;
      const originalTotalAmount = c.totalPerPax[passenger.id]?.price?.originalTotalAmount;
      return {
        id: c.category,
        total: totalAmount ?? null,
        originalTotal: originalTotalAmount ?? null,
        count,
        key: `purchase.payment.${name}`,
        [name]: count,
      };
    })
    .filter((service) => service.count > 0)
    .sort((a, b) => sortOrder.indexOf(b.id) - sortOrder.indexOf(a.id));
};

export const getCategoryServices = (
  pricesForCategory: FinnairServiceCategoryPriceItem[] = [],
  ancillaryCategory: Category
): FinnairServiceCategoryPriceItem => {
  return pricesForCategory.find((categories) => categories.category === ancillaryCategory);
};

export const showTravelExtrasTitle = (pricesForCategory: FinnairServiceCategoryPriceItem[] = []): boolean => {
  const hasSaf = pricesForCategory.some((item) => item.category === Category.SAF);
  const hasOtherAncillaries = pricesForCategory.some((item) => item.category !== Category.SAF);

  return !hasSaf || hasOtherAncillaries;
};

export const passengerHasSaf = (
  pricesForCategory: FinnairServiceCategoryPriceItem[] = [],
  passenger: FinnairPassengerItem
): boolean => {
  return (
    pricesForCategory.length &&
    pricesForCategory.some((service) => service.category === Category.SAF && service.totalPerPax[passenger.id])
  );
};

export const passengerHasOtherServices = (
  pricesForCategory: FinnairServiceCategoryPriceItem[] = [],
  passenger: FinnairPassengerItem
): boolean => {
  return (
    pricesForCategory.length &&
    pricesForCategory.reduce((acc, service) => {
      return acc || Boolean(service.category !== Category.SAF && service.totalPerPax[passenger.id]);
    }, false)
  );
};

export const getThirdPartyEligibility = (order: FinnairOrder, eligibility: ThirdPartyEligibilityKey): boolean => {
  return Boolean(
    order.eligibilities?.[eligibility]?.some((eligibility) => {
      return eligibility.id === 'JOURNEY' && eligibility.isAllowedToUse;
    })
  );
};

export const getEligibleAllianzInsuranceId = (
  order$: Observable<FinnairOrder>
): Observable<TravelInsuranceEligibilityId | null> => {
  return order$.pipe(
    map((booking) => {
      const travelInsurances = booking?.eligibilities?.travelInsurance ?? [];
      const insurance = travelInsurances.find((item) => item.isAllowedToUse);

      if (insurance) {
        return insurance.id as TravelInsuranceEligibilityId;
      }

      return null;
    })
  );
};
